import React from 'react';
import { 
  LuChevronRight, 
  LuFileText, 
  LuInfo 
} from 'react-icons/lu';

// Use the Feather pack for status and alert indicators to guarantee a successful compilation
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertTriangle 
} from 'react-icons/fi';


export default function FireSafetyAuditFooterDashboard() {
  // Data for Key Documents & Evidence List
  const documents = [
    { name: 'Fire Risk Assessment Report', type: 'Issued', date: '18 Mar 2026' },
    { name: 'Fire Alarm Test Log (Weekly)', type: 'Updated', date: '18 Jul 2026' },
    { name: 'Emergency Lighting Test Log', type: 'Updated', date: '10 Jul 2026' },
    { name: 'Fire Extinguisher Inspection Log', type: 'Updated', date: '12 Jul 2026' },
    { name: 'Fire Door Inspection Log', type: 'Updated', date: '01 Jun 2026' },
    { name: 'Fire Safety Policy', type: 'Issued', date: '20 Jan 2026' },
  ];

  // Data for Recent Activity List
  const activities = [
    { text: 'Weekly Fire Alarm Test Completed', location: 'Kitchen', date: '18 Jul 2026', status: 'completed' },
    { text: 'Extinguisher Inspection Completed', location: 'Bar Area', date: '12 Jul 2026', status: 'completed' },
    { text: 'Fire Door Inspection Due Soon', location: 'Dining Area', date: '20 Jul 2026', status: 'warning' },
    { text: 'Fire Alarm Test Overdue', location: 'Kitchen', date: '11 Jul 2026', status: 'danger' },
    { text: 'Emergency Lighting Test Completed', location: 'Office', date: '10 Jul 2026', status: 'completed' },
  ];

  // Data for Information for Inspector List
  const inspectorInfos = [
    'All required fire safety items are recorded above',
    'Test and inspection frequencies follow UK fire safety legislation',
    <>Certificates and logs are available in <span className="font-extrabold text-slate-900">Compliance Documents</span></>,
    'Any overdue items are highlighted for immediate action',
    'Contact the manager if further information is required',
  ];

  return (
    <div className="w-full bg-slate-50 p-6 font-sans flex flex-col gap-6">
      
      {/* 3-Column Card Layout Block */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1800px] mx-auto items-stretch w-full">
        
        {/* Card 1: Key Documents & Evidence */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-5">
              Key Documents & Evidence
            </h2>
            <div className="flex flex-col gap-3.5">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between text-[11px] font-semibold">
                  <div className="flex items-center gap-2.5 text-indigo-950">
                    <LuFileText className="text-indigo-600 text-sm flex-shrink-0" />
                    <span>{doc.name}</span>
                  </div>
                  <span className="text-slate-400 font-normal ml-2 whitespace-nowrap">
                    {doc.type}: {doc.date}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all documents</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 2: Recent Activity Table Grid */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-4">
              Recent Activity
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-indigo-50/40 text-[9px] font-bold text-indigo-950/70 tracking-wider">
                    <th className="p-2.5 rounded-l-lg w-[55%]">ACTIVITY</th>
                    <th className="p-2.5">LOCATION</th>
                    <th className="p-2.5 text-right rounded-r-lg">DATE</th>
                  </tr>
                </thead>
                <tbody className="text-[11px] font-semibold text-slate-700 divide-y divide-slate-50/60">
                  {activities.map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50/40 transition-colors">
                      <td className="p-2.5 flex items-center gap-2 font-bold text-slate-800">
                        {/* CHANGED FROM Lu TO Fi FOR THE STATUS CODES BELOW */}
                        {row.status === 'completed' && <FiCheckCircle className="text-emerald-600 text-sm flex-shrink-0" />}
                        {row.status === 'warning' && <FiClock className="text-orange-500 text-sm flex-shrink-0" />}
                        {row.status === 'danger' && <FiAlertTriangle className="text-rose-600 text-sm flex-shrink-0" />}
                        <span className="truncate max-w-[180px] lg:max-w-none">{row.text}</span>
                      </td>
                      <td className="p-2.5 text-slate-500 font-medium">{row.location}</td>
                      <td className="p-2.5 text-right text-slate-900 font-medium whitespace-nowrap">{row.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all activity</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 3: Information for Inspector */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-5">
              Information for Inspector
            </h2>
            <div className="flex flex-col gap-3.5">
              {inspectorInfos.map((item, index) => (
                <div key={index} className="flex items-start gap-2.5 text-[11px] font-semibold text-slate-700 leading-tight">
                  <LuInfo className="text-blue-600 text-sm mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View policy & guidance</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

      </div>

      {/* Bottom Informational Footer Alert Bar */}
      <div className="w-full max-w-[1800px] mx-auto bg-indigo-50/40 border border-indigo-100/50 rounded-xl px-4 py-3 flex items-center gap-2.5">
        <LuInfo className="text-blue-600 text-sm flex-shrink-0" />
        <p className="text-[10.5px] font-medium text-slate-500 tracking-wide">
          All fire safety records are time stamped and securely stored for compliance and audit purposes.
        </p>
      </div>

    </div>
  );
}
